<script lang="ts">
	import { onMount } from 'svelte';

	interface Props {
		id?: string | null;
		text: string;
		disabled?: boolean;
		secondary?: boolean;
		warning?: boolean;
		active?: boolean;
		autofocus?: boolean;
		spamProtectedCall?: (() => Promise<any>) | null;
		onClick?: (e: MouseEvent) => void;
	}

	let {
		id = null,
		text,
		disabled = false,
		secondary = false,
		warning = false,
		active = false,
		autofocus = false,
		spamProtectedCall = null,
		onClick = undefined
	}: Props = $props();

	let spamProtected = $state(false);
	let button: HTMLButtonElement | undefined = $state();

	onMount(() => {
		if (button && autofocus) {
			button.focus();
		}
	});
</script>

<button
	bind:this={button}
	{id}
	class="shadow-lg button rounded p-3 px-6"
	class:disabled
	class:active
	class:warning-button={warning && !disabled}
	class:primary-button={!secondary && !disabled && !warning}
	class:secondary-button={secondary && !disabled && !warning}
	{disabled}
	onclick={(e) => {
		onClick?.(e);

		if (spamProtectedCall) {
			//Prevent the call
			if (spamProtected) return;

			//Set as running
			spamProtected = true;

			//Call the callable
			spamProtectedCall()
				//Add small delay for reactivation
				.then(() => new Promise((resolve) => setTimeout(resolve, 200)))
				.finally(() => {
					//Revert the runnung flag
					spamProtected = false;
				});
		}
	}}
>
	{text}
</button>

<!-- svelte-ignore css_unused_selector-->
<style>
	.button {
		outline: none;
		min-width: 100px;
	}

	.secondary-button :global {
		border-width: 1px;
		--tw-border-opacity: 1;
		border-color: rgb(44 58 73 / var(--tw-border-opacity, 1));
		--tw-text-opacity: 1;
		color: rgb(44 58 73 / var(--tw-text-opacity, 1));
}

	.secondary-button :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(146 188 220 / var(--tw-border-opacity, 1));
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.secondary-button:focus,
	.secondary-button:hover,
	.secondary-button.active :global {
		--tw-border-opacity: 1;
		border-color: rgb(23 137 192 / var(--tw-border-opacity, 1));
		--tw-text-opacity: 1;
		color: rgb(23 137 192 / var(--tw-text-opacity, 1));
}

	.secondary-button:focus:where(.dark, .dark *),
	.secondary-button:hover:where(.dark, .dark *),
	.secondary-button.active :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(125 125 125 / var(--tw-border-opacity, 1));
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.warning-button {
		--tw-bg-opacity: 1;
		background-color: rgb(168 7 39 / var(--tw-bg-opacity, 1));
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.warning-button:focus,
	.warning-button:hover,
	.warning-button.active {
		--tw-bg-opacity: 1;
		background-color: rgb(130 7 31 / var(--tw-bg-opacity, 1));
}

	.disabled :global {
		--tw-bg-opacity: 1;
		background-color: rgb(242 242 242 / var(--tw-bg-opacity, 1));
}

	.disabled :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(101 101 101 / var(--tw-bg-opacity, 1));
}

	.primary-button :global {
		--tw-bg-opacity: 1;
		background-color: rgb(44 58 73 / var(--tw-bg-opacity, 1));
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.primary-button :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(146 188 220 / var(--tw-bg-opacity, 1));
}

	.primary-button:focus,
	.primary-button:hover,
	.primary-button.active :global {
		--tw-bg-opacity: 1;
		background-color: rgb(23 137 192 / var(--tw-bg-opacity, 1));
}

	.primary-button:focus:where(.dark, .dark *),
	.primary-button:hover:where(.dark, .dark *),
	.primary-button.active :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(56 56 56 / var(--tw-bg-opacity, 1));
}

	button:disabled {
		cursor: default;
	}
</style>
